import {
  Modal,
  NewButton,
  NewButtonVariant,
} from "@visiblethread/react-components";
import Flex, { GAP_CLASS } from "components/Flex";
import { DIRECTION_CLASS } from "components/Flex/Flex";
import RadioButton from "components/RadioButton";
import { func, number, oneOf } from "prop-types";
import { useState } from "react";
import { isAreVerb, pluralize } from "utils/string";

import classes from "./CascadeAssigneeModal.module.scss";

export const cascadeAssigneeModes = {
  assign: "assign",
  unassign: "unassign",
};

const countWithNoun = (count, noun) => `${count} ${pluralize(noun, count)}`;
const countText = (subsectionCount) =>
  `This section contains ${countWithNoun(subsectionCount, "sub-section")}.`;

const MODE_TEXT = {
  [cascadeAssigneeModes.assign]: {
    headerText: "Assign",
    confirmButtonText: "Assign",
    yesOptionLabel: "Yes, assign to sub-sections",
    noOptionLabel: "No, assign to this section only",
    questionText: "Do you want to also assign this user to the sub-sections?",
    secondaryText: (secondaryCount) =>
      `${secondaryCount} of them ${isAreVerb(secondaryCount)} already assigned to this user.`,
  },
  [cascadeAssigneeModes.unassign]: {
    headerText: "Unassign",
    confirmButtonText: "Unassign",
    yesOptionLabel: "Yes, unassign from the sub-sections",
    noOptionLabel: "No, unassign from this section only",
    questionText:
      "Do you want to also unassign this user from the sub-sections?",
    secondaryText: (secondaryCount) =>
      `${secondaryCount} of them ${isAreVerb(secondaryCount)} assigned to this user.`,
  },
};

const CascadeAssigneeModal = ({
  mode,
  subsectionCount,
  secondaryCount = 0,
  onConfirm,
  onCancel,
}) => {
  const [cascade, setCascade] = useState(true);
  const {
    headerText,
    confirmButtonText,
    yesOptionLabel,
    noOptionLabel,
    questionText,
    secondaryText,
  } = MODE_TEXT[mode];

  const renderOption = (label, value) => (
    <button
      type="button"
      className={classes["cascade_assignee_modal__option"]}
      role="radio"
      aria-checked={cascade === value}
      onClick={() => setCascade(value)}
    >
      <Flex gap={GAP_CLASS.GAP_250}>
        <RadioButton checked={cascade === value} />
        <span>{label}</span>
      </Flex>
    </button>
  );

  return (
    <Modal
      data-testid="cascade-assignee-modal"
      className="fade-in"
      show
      onDismiss={onCancel}
      centered
    >
      <Modal.Header>{headerText}</Modal.Header>
      <Modal.Body>
        <Flex
          direction={DIRECTION_CLASS.column}
          gap={GAP_CLASS.GAP_400}
          alignItems="flex-start"
        >
          <Flex
            className={classes["cascade_assignee_modal__text_group"]}
            direction={DIRECTION_CLASS.column}
            gap={GAP_CLASS.GAP_200}
            alignItems="flex-start"
          >
            <p>{countText(subsectionCount)}</p>
            {secondaryCount > 0 && (
              <p className={classes["cascade_assignee_modal__secondary_text"]}>
                {secondaryText(secondaryCount)}
              </p>
            )}
            <p>{questionText}</p>
          </Flex>

          <Flex
            direction={DIRECTION_CLASS.column}
            gap={GAP_CLASS.GAP_300}
            alignItems="flex-start"
          >
            {renderOption(yesOptionLabel, true)}
            {renderOption(noOptionLabel, false)}
          </Flex>
        </Flex>
      </Modal.Body>
      <Modal.Footer>
        <NewButton
          data-testid="cascade-assignee-modal-cancel"
          variant={NewButtonVariant.l2}
          onClick={onCancel}
        >
          Cancel
        </NewButton>
        <NewButton
          data-testid="cascade-assignee-modal-confirm"
          variant={NewButtonVariant.l1}
          onClick={() => onConfirm(cascade)}
        >
          {confirmButtonText}
        </NewButton>
      </Modal.Footer>
    </Modal>
  );
};

CascadeAssigneeModal.propTypes = {
  mode: oneOf(Object.values(cascadeAssigneeModes)).isRequired,
  subsectionCount: number.isRequired,
  secondaryCount: number,
  onConfirm: func.isRequired,
  onCancel: func.isRequired,
};

export default CascadeAssigneeModal;
